import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Tab, Tabs} from "@mui/material";
import {Add, OpenInNew} from "@mui/icons-material";
import {useTranslation} from "react-i18next";
import {useHistory} from "react-router-dom";
import {DefaultPaginationInfo} from "../../../../../../../core/constants/enums";
import {useDataGridSavedState} from "../../../../../../../packages/data-grid";
import useSearch from "../../../../../../hooks/use-search";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import PurchasesSearchSection from "../../../../../../components/search-sections/purchases";
import CreateVendorDialog from "../../../../../../components/dialogs/create-vendor";
import AddPurchaseDialog from "../../../../../../components/dialogs/add-purchase";
import {ListPageShell, PageActions} from "../../../../../../components/page-shell";
import {routeFunctions, routes} from "../../../../../../../core/models/static/routes/app-routes";
import Utils, {formatMoney} from "../../../../../../../core/services/utils/utils";
import {useConfigureNotebook} from "../../../../../../hooks/use-notebook-config";
import useBehavioralAnalyticsTool from "../../../../../../hooks/use-behavioural-analytics-tool";
import {useAppSelector} from "../../../../../../redux";
import useAccessLevels from "../../../../../../hooks/use-access-levels";
import {PurchaseStatuses} from "../../constants";
import PurchaseOrdersListOrdersSection from "./sections/orders";
import PurchaseOrdersListItemsSection from "./sections/items";

const tabs = {orders: 'orders', items: 'items'};

const ordersSaveConfig = {name: 'PurchaseOrdersListOrdersDataGrid', version: 2};
const itemsSaveConfig = {name: 'PurchaseOrdersListItemsDataGrid', version: 1};

const initVendorOnlineProfileDialog = {vendor: null, open: false, purchaseId: null};
const initNewPurchaseDialog = {open: false};

const orderStatusCycle = [PurchaseStatuses.open, PurchaseStatuses.pending, PurchaseStatuses.closed];

/**
 * Compares a row date string (YYYY-MM-DD…) against optional shell start/end filters.
 */
const rowDateInRange = (dateStr, startDate, endDate) => {
    if (!startDate && !endDate) {
        return true;
    }
    const day = dateStr?.toString()?.slice(0, 10);
    if (!day) {
        return true;
    }
    const start = startDate?.toString()?.slice(0, 10);
    const end = endDate?.toString()?.slice(0, 10);
    if (start && day < start) {
        return false;
    }
    if (end && day > end) {
        return false;
    }
    return true;
};

/** Shell mocks until CleanAF PO APIs exist. */
const mockOrders = (scopeKey) => Array.from({length: 8}, (_, i) => ({
    id: i + 1,
    orderNo: `PO-${scopeKey}-${1000 + i}`,
    date: `2026-01-${String((i % 28) + 1).padStart(2, '0')}`,
    status: orderStatusCycle[i % orderStatusCycle.length],
    total: 1200 + i * 85,
    currency: 'CAD',
    vendor: {id: Number(scopeKey) || i + 1, name: `Vendor ${i + 1}`, title: `Vendor ${i + 1}`},
    vendorId: Number(scopeKey) || i + 1,
}));

const mockItems = (scopeKey) => Array.from({length: 12}, (_, i) => ({
    id: i + 1,
    orderId: (i % 8) + 1,
    vendorPartNo: `VP-${200 + i}`,
    partNo: `P-${500 + i}`,
    description: `Part line ${i + 1}`,
    qty: (i % 5) + 1,
    unitPrice: 25 + i,
    total: ((i % 5) + 1) * (25 + i),
    serials: i % 3 === 0 ? `SN-${scopeKey}-${i}` : '',
    date: `2026-01-${String((i % 28) + 1).padStart(2, '0')}`,
    status: orderStatusCycle[i % orderStatusCycle.length],
    vendor: {id: Number(scopeKey) || i + 1, name: `Vendor ${i + 1}`, title: `Vendor ${i + 1}`},
    vendorId: Number(scopeKey) || i + 1,
}));

/**
 * Dual-scope purchase orders list (system + vendor).
 * // TODO: replace mocks with CleanAF purchase-order APIs.
 */
const PurchaseOrdersListView = () => {
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.purchases.ordersList'});
    const history = useHistory();
    const defaultVendor = useAppSelector((state) => state.vendorInformation);
    const operatorAccessLevels = useAccessLevels();
    const [tab, setTab] = useState(tabs.orders);
    const [data, setData] = useState([]);
    const [newPurchaseDialog, setNewPurchaseDialog] = useState(initNewPurchaseDialog);
    const [vendorOnlineProfileDialog, setVendorOnlineProfileDialog] = useState(initVendorOnlineProfileDialog);
    const isMounted = useIsMounted();
    const {configureNotebookTags} = useConfigureNotebook('purchase-orders-list');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();

    const [, ordersSortBy, ordersPageSize] = useDataGridSavedState({
        columns: [],
        ...ordersSaveConfig,
    });
    const [, itemsSortBy, itemsPageSize] = useDataGridSavedState({
        columns: [],
        ...itemsSaveConfig,
    });

    /**
     * Notebook tags: vendor-scoped or system list.
     */
    useEffect(() => {
        if (defaultVendor?.code) {
            configureNotebookTags((tags) => tags.vendorPurchaseOrders(defaultVendor.code));
            configureBehavioralTool((tags) => tags.vendorPurchaseOrders(defaultVendor.code));
        } else {
            configureNotebookTags((tags) => tags.purchaseList);
            configureBehavioralTool((tags) => tags.purchaseList);
        }
    }, [configureBehavioralTool, configureNotebookTags, defaultVendor?.code]);

    const prepareSearchData = useCallback((args) => ({
        keyword: args.filters?.keyword?.toString() ?? '',
        partNo: args.filters?.partNo?.toString() ?? '',
        paginationInfo: args.paginationInfo,
        tab: args.tab ?? tab,
        vendorId: defaultVendor?.id,
        vendors: defaultVendor?.id ? [] : (args.vendors ?? []),
        status: args.filters?.status,
        startDate: args.filters?.startDate,
        endDate: args.filters?.endDate,
    }), [defaultVendor?.id, tab]);

    /**
     * Shell search over mock data. // TODO: live APIs
     */
    const search = useCallback(async (args) => {
        const request = prepareSearchData(args);
        await Utils.wait(200);
        if (!isMounted()) return;
        const scopeKey = request.vendorId ?? 'sys';
        const source = request.tab === tabs.items
            ? mockItems(scopeKey)
            : mockOrders(scopeKey);
        const keyword = request.keyword.trim().toLowerCase();
        const partNo = request.partNo.trim().toLowerCase();
        let filtered = source;
        if (keyword) {
            filtered = filtered.filter((row) => Object.values(row).some((v) =>
                typeof v !== 'object' && String(v).toLowerCase().includes(keyword)));
        }
        if (partNo && request.tab === tabs.items) {
            filtered = filtered.filter((row) =>
                String(row.partNo ?? '').toLowerCase().includes(partNo)
                || String(row.vendorPartNo ?? '').toLowerCase().includes(partNo));
        }
        if (!request.vendorId && request.vendors?.length) {
            const ids = new Set(request.vendors.map((v) => v.id));
            filtered = filtered.filter((row) => ids.has(row.vendor?.id ?? row.vendorId));
        }
        if (request.status) {
            filtered = filtered.filter((row) =>
                String(row.status ?? '').toLowerCase() === String(request.status).toLowerCase());
        }
        filtered = filtered.filter((row) => rowDateInRange(row.date, request.startDate, request.endDate));
        const start = (request.paginationInfo.currentPage - 1) * request.paginationInfo.pageSize;
        setData(filtered.slice(start, start + request.paginationInfo.pageSize));
        args.setPaginationInfo((prev) => ({...prev, length: filtered.length}));
    }, [isMounted, prepareSearchData]);

    const initialPaginationInfo = useMemo(() => ({
        ...DefaultPaginationInfo,
        pageSize: (tab === tabs.items ? itemsPageSize : ordersPageSize) ?? DefaultPaginationInfo.pageSize,
    }), [itemsPageSize, ordersPageSize, tab]);

    const searchData = useSearch(search, {
        paginationInfo: initialPaginationInfo,
        orderBy: tab === tabs.items ? itemsSortBy : ordersSortBy,
        tab,
        vendors: [],
    });

    useEffect(() => {
        searchData.setTab(tab);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab]);

    /**
     * Opens vendor PO single; system list may prompt for vendor online profile.
     */
    const navigateToOrder = useCallback((purchase, vendorId) => {
        const resolvedVendorId = vendorId ?? purchase.vendor?.id ?? purchase.vendorId;
        if (!resolvedVendorId) {
            return;
        }
        if (defaultVendor?.id) {
            history.push(routeFunctions.vendor.purchases.orders.single(resolvedVendorId, purchase.id));
            return;
        }
        // TODO: call API for online profile
        const hasOnlineProfile = true;
        if (hasOnlineProfile) {
            history.push(routeFunctions.vendor.purchases.orders.single(resolvedVendorId, purchase.id));
            return;
        }
        setVendorOnlineProfileDialog({
            open: true,
            vendor: purchase.vendor,
            purchaseId: purchase.id,
        });
    }, [defaultVendor?.id, history]);

    const onVendorOnlineProfileCreated = useCallback((vendor) => {
        history.push(routeFunctions.vendor.purchases.orders.single(
            vendor?.id,
            vendorOnlineProfileDialog.purchaseId,
        ));
    }, [history, vendorOnlineProfileDialog.purchaseId]);

    /**
     * Tab change resets data and swaps grid page size / sort.
     */
    const onTabChanged = useCallback((_e, newTab) => {
        setData([]);
        setTab(newTab);
        searchData.setTab(newTab);
        if (newTab === tabs.items) {
            searchData.setPaginationInfo((prev) => ({
                ...prev,
                pageSize: itemsPageSize ?? initialPaginationInfo.pageSize,
            }));
            searchData.setOrderBy(itemsSortBy);
        } else {
            searchData.setPaginationInfo((prev) => ({
                ...prev,
                pageSize: ordersPageSize ?? initialPaginationInfo.pageSize,
            }));
            searchData.setOrderBy(ordersSortBy);
        }
    }, [initialPaginationInfo.pageSize, itemsPageSize, itemsSortBy, ordersPageSize, ordersSortBy, searchData]);

    const canExportOrdersList = operatorAccessLevels.packages.purchasing.exportPurchaseOrdersList;

    /**
     * Builds CSV export payload for the orders tab.
     */
    const exportOrdersToCsv = useCallback(async () => ({
        title: t('exportTitleOrders'),
        body: data.map((row) => ({
            [t('columns.orderNo')]: row.orderNo ?? row.purchaseNo ?? '',
            [t('columns.date')]: row.date ?? row.submittedDateTime ?? '',
            [t('columns.status')]: row.status ?? row.statusTitle ?? '',
            [t('columns.total')]: formatMoney(row.total),
            [t('columns.currency')]: row.currency ?? 'CAD',
            ...(!defaultVendor?.id ? {
                [t('columns.vendor')]: row.vendor?.name ?? row.vendor?.title ?? '',
            } : {}),
        })),
    }), [data, defaultVendor?.id, t]);

    /**
     * Builds CSV export payload for the items tab.
     */
    const exportItemsToCsv = useCallback(async () => ({
        title: t('exportTitleItems'),
        body: data.map((row) => ({
            [t('columns.vendorPartNo')]: row.vendorPartNo ?? '',
            [t('columns.partNo')]: row.partNo ?? '',
            [t('columns.description')]: row.description ?? '',
            [t('columns.qty')]: row.qty ?? '',
            [t('columns.unitPrice')]: formatMoney(row.unitPrice),
            [t('columns.total')]: formatMoney(row.total),
            [t('columns.serials')]: row.serials ?? '',
        })),
    }), [data, t]);

    const title = defaultVendor?.name
        ? t('titleVendor', {name: defaultVendor.name})
        : t('titleSystem');

    const actionItems = useMemo(() => ([
        {
            id: 'view-all',
            label: t('viewAll'),
            icon: <OpenInNew className={'icon small'}/>,
            hidden: !defaultVendor?.id,
            onClick: () => history.push(routes.purchase.orders),
        },
        {
            id: 'add-purchase',
            label: t('addPurchase'),
            icon: <Add className={'icon small'}/>,
            hidden: !operatorAccessLevels.packages.purchasing.submitPurchaseOrder,
            onClick: () => setNewPurchaseDialog({open: true}),
        },
    ]), [defaultVendor?.id, history, operatorAccessLevels.packages.purchasing.submitPurchaseOrder, t]);

    const filters = useMemo(() => (
        <PurchasesSearchSection
            searchData={searchData}
            hideVendorsMultiSelect={!!defaultVendor?.id}
            showPartNumberField={tab === tabs.items}
            statusVariant={'orders'}
        />
    ), [defaultVendor?.id, searchData, tab]);

    return (
        <>
            <ListPageShell
                title={title}
                subtitle={t('shellNotice')}
                actions={<PageActions items={actionItems}/>}
                filters={filters}
                tabs={(
                    <Tabs value={tab} onChange={onTabChanged}>
                        <Tab value={tabs.orders} label={t('tabs.orders')}/>
                        <Tab value={tabs.items} label={t('tabs.items')}/>
                    </Tabs>
                )}
            >
                {
                    tab === tabs.orders &&
                    <PurchaseOrdersListOrdersSection
                        data={data}
                        searchData={searchData}
                        dataGridSaveConfig={ordersSaveConfig}
                        navigateToOrder={navigateToOrder}
                        defaultVendor={defaultVendor}
                        getExportInfo={canExportOrdersList ? exportOrdersToCsv : undefined}
                    />
                }
                {
                    tab === tabs.items &&
                    <PurchaseOrdersListItemsSection
                        data={data}
                        searchData={searchData}
                        dataGridSaveConfig={itemsSaveConfig}
                        getExportInfo={canExportOrdersList ? exportItemsToCsv : undefined}
                    />
                }
            </ListPageShell>
            <CreateVendorDialog
                open={vendorOnlineProfileDialog.open}
                close={() => setVendorOnlineProfileDialog(initVendorOnlineProfileDialog)}
                selectedVendor={{
                    phone: vendorOnlineProfileDialog.vendor?.phone,
                    email: vendorOnlineProfileDialog.vendor?.email,
                    vendor: vendorOnlineProfileDialog.vendor,
                }}
                onSuccess={onVendorOnlineProfileCreated}
            />
            <AddPurchaseDialog
                open={newPurchaseDialog.open}
                setOpen={(nextOpen) => setNewPurchaseDialog(nextOpen ? {open: true} : initNewPurchaseDialog)}
                vendor={defaultVendor?.id ? defaultVendor : null}
            />
        </>
    );
};

export default PurchaseOrdersListView;
